/**
 * *************************************
 *  SHADAPPS CONFIDENTIAL
 *  __________________
 *
 *  Created by Vittorio Tauro
 *
 *  2022 (c) ShadApps Srl
 *  All Rights Reserved.
 *
 *  NOTICE:  All information contained herein is, and remains
 *  the property of ShadApps Srl and its suppliers,
 *  if any. The intellectual and technical concepts contained
 *  herein are proprietary to ShadApps Srl.
 *  and its suppliers and may be covered by Italian, European and Foreign Patents,
 *  patents in process, and are protected by trade secret or copyright law.
 *  Dissemination of this information or reproduction of this material
 *  is strictly forbidden unless prior written permission is obtained
 *  from ShadApps Srl.
 * *************************************
 * */

import { createSlice } from '@reduxjs/toolkit';
// utils
import { useDispatch } from 'react-redux';
import axios from '../../utils/axios';
//
// routes
import { PATH_API } from '../../routes/api';

import { entities } from '../../json/sections/sectionsJson';

// ----------------------------------------------------------------------

const reduxDB = entities.reduce((db, entity) => {
    console.log(); // debug purpose. to not use direct returning
    db[entity] = {
        entity,
        crudObjects: [],
        crudObject: {},
        selectedId: '',
    };
    return db;
}, {});

const initialState = {
    isLoading: false,
    error: null,
    success: false,
    reduxDB,
    isOpenModal: false,
    selectedId: null,
};

const slice = createSlice({
    name: 'crud',
    initialState,
    reducers: {
        // START LOADING
        startLoading(state) {
            state.success = false;
            state.isLoading = true;
        },

        // HAS ERROR
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },
        resetError(state, action) {
            state.error = null;
        },
        // GET  crudS
        getCrudsSuccess(state, action) {
            const { entity, crudObjects } = action.payload;
            state.isLoading = false;
            state.reduxDB[entity].crudObjects = crudObjects;
        },
        // GET  crudS
        getCrudSuccess(state, action) {
            const { entity, crudObject } = action.payload;
            state.isLoading = false;
            state.reduxDB[entity].crudObject = crudObject;
        },

        setOne(state, action) {
            const { entity, crudObject } = action.payload;
            state.reduxDB[entity].crudObject = crudObject;
        },

        setAll(state, action) {
            const { entity, crudObjects } = action.payload;
            state.reduxDB[entity].crudObjects = crudObjects;
        },
        updateOneInRedux(state, action) {
            const { entity, updatedCrudObject } = action.payload;
            const newArray = state.reduxDB[entity].crudObjects.map((crud) => {
                if (crud._id === updatedCrudObject._id) {
                    return updatedCrudObject;
                }
                return crud;
            });
            state.reduxDB[entity].crudObjects = newArray;
        },
        // CREATE   crud
        createCrudSuccess(state, action) {
            const { entity, newCrudObject } = action.payload;
            state.success = true;
            state.isLoading = false;
            state.reduxDB[entity].crudObjects = [
                newCrudObject,
                ...state.reduxDB[entity].crudObjects,
            ];
        },

        // UPDATE   crud
        updateCrudSuccess(state, action) {
            const { crudObject, entity, crudId } = action.payload;
            // predefine updatedObject array for operation
            let updatedCrud = [];
            // case the crudObject is actually array of crudObjects
            if (Array.isArray(crudObject)) {
                const ids = crudObject.map((obj) => obj._id);
                while (ids.length) {
                    const updatingId = ids[0];
                    updatedCrud = state.reduxDB[entity].crudObjects.map(
                        (_crud) => {
                            if (_crud._id.toString() === updatingId) {
                                return crudObject; // return the updated object
                            }
                            return _crud; // return the same object
                        }
                    );
                    ids.shift();
                }
                // else case is general case. update one by one
            } else {
                updatedCrud = state.reduxDB[entity].crudObjects.map((_crud) => {
                    if (_crud._id.toString() === crudId) {
                        return crudObject; // return the updated object
                    }
                    return _crud; // return the same object
                });
            }

            state.success = true;
            state.isLoading = false;
            state.reduxDB[entity].crudObjects = updatedCrud;
        },

        // DELETE   crud
        deleteCrudSuccess(state, action) {
            const { crudId, entity } = action.payload;
            const deleteCrud = state.reduxDB[entity].crudObjects.filter(
                (crud) => crud._id !== crudId
            );
            state.reduxDB[entity].crudObjects = deleteCrud;
            state.isLoading = false;
            state.success = true;
        },

        // SELECT   crud
        selectCrud(state, action) {
            const { crudId, entity } = action.payload;
            state.isOpenModal = true;
            state.error = null;
            state.success = false;
            state.reduxDB[entity].selectedId = crudId;
        },
    },
});

// Reducer
export default slice.reducer;

export const { selectCrud, setAll, setOne, updateOneInRedux } = slice.actions;
// ----------------------------------------------------------------------
export function getCruds({ entity, query = '', queryObj }) {
    if (!PATH_API[entity]) {
        window.alert(`PATH_API is not set for the entity ${entity}`);
    }
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            // const queryString = query ? `?${query}`: '';
            const response = await axios.get(
                `${PATH_API[entity]}${query ?? ''}`,
                { params: queryObj }
            ); // get entity
            dispatch(
                slice.actions.getCrudsSuccess({
                    crudObjects: response.data.data,
                    entity,
                })
            );
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

// ----------------------------------------------------------------------
/** need to configure when it is necessary */
export function getCrud({ entity, objectId = '', query = '', queryObj }) {
    if (!PATH_API[entity]) {
        window.alert(`PATH_API is not set for the entity ${entity}`);
    }
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        const idPath = objectId ? `/${objectId}` : '';
        try {
            const response = await axios.get(
                `${PATH_API[entity]}${idPath}${query ?? ''}`,
                { params: queryObj }
            );
            // intended to get one object if the api return an array return the first object
            const data = idPath
                ? response.data.data
                : response.data.data[0] || {};
            dispatch(
                slice.actions.getCrudSuccess({
                    crudObject: data,
                    entity,
                })
            );
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

// ----------------------------------------------------------------------

export function createCrud({ entity, newObject }) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await axios.post(PATH_API[entity], newObject);
            dispatch(
                slice.actions.createCrudSuccess({
                    newCrudObject: response.data.data,
                    entity,
                })
            );
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

// ----------------------------------------------------------------------

export function updateCrud({ entity, crudId, updates }) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await axios.post(
                `${PATH_API[entity]}/${crudId}`,
                updates
            );
            dispatch(
                slice.actions.updateCrudSuccess({
                    crudObject: response.data.data,
                    entity,
                    crudId,
                })
            );
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

// ----------------------------------------------------------------------

export function deleteCrud({ entity, crudId }) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            await axios.delete(`${PATH_API[entity]}/${crudId}`);
            dispatch(slice.actions.deleteCrudSuccess({ crudId, entity }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

/** now use Redux Action is no longer needed with this hook */
export const useCrudSlice = () => {
    const dispatch = useDispatch();
    return {
        resetError: () => dispatch(slice.actions.resetError()),
        deleteCrud: ({ entity, crudId }) =>
            dispatch(deleteCrud({ entity, crudId })),
        updateCrud: ({ entity, crudId, updates }) =>
            dispatch(updateCrud({ entity, crudId, updates })),
        createCrud: ({ entity, newCrud }) =>
            dispatch(createCrud({ entity, newCrud })),
        getCrud: ({ entity, query, objectId, queryObj }) =>
            dispatch(getCrud({ entity, query, objectId, queryObj })),
        getCruds: ({ entity, query, queryObj }) =>
            dispatch(getCruds({ entity, query, queryObj })),
        selectCrud: ({ crudId, entity }) =>
            dispatch(selectCrud({ crudId, entity })),
        createCrudSuccess: ({ entity, newCrudObject }) =>
            dispatch(
                slice.actions.createCrudSuccess({ entity, newCrudObject })
            ),
        updateCrudSuccess: ({ crudObject, entity, crudId }) =>
            dispatch(
                slice.actions.updateCrudSuccess({ crudObject, entity, crudId })
            ),
    };
};
